/**
 * biome_settlements.ts — per-civilization prefab palettes + deterministic selectors.
 *
 * Maps the engine CityGenerator's abstract programs (`house|apartment|office|
 * shop|warehouse|factory|skyscraper`), BuildingFunctions, and street-prop types
 * onto cooked `structure:civa_*` / `structure:civb_*` / `prop:*` prefab keys.
 * Each settlement deterministically adopts ONE civilization pack (see
 * `civilizationOf`) and draws every building + landmark from it — packs are
 * never mixed within a city/town. Street props stay pack-independent
 * (Higgsfield furniture; StreetLampsModule keys off STREETLIGHT_KEYS).
 *
 * Selection is a pure function of `(civ, program/fn, h)` (+ a biome salt) where
 * `h ∈ [0,1)` is a deterministic per-placement hash — no Math.random, so every
 * client renders the same building on the same parcel.
 */
import { BIOMES } from '../../../shared/terrain.js';
import type { BuildingFunction } from 'enginex/city/buildings/BuildingFunction.js';

type Program = 'house' | 'apartment' | 'office' | 'shop' | 'warehouse' | 'factory' | 'skyscraper';

// ── Civilization collections (2026-07-19 structure swap) ──────────────────────
// Settlement buildings come from TWO user-supplied GLB packs cooked through the
// asset pipeline (tools/asset-pipeline/ingest-civilizations.mjs):
//   civ 'a' — atlas/structures/civilization-A ("Epic Sci-Fi" towers + named specials)
//   civ 'b' — atlas/structures/civilization-B ("FutureCity" towers + named specials)
// Every settlement picks ONE civilization deterministically from its siteSeed and
// draws ALL of its buildings + landmarks from that pack — collections are never
// mixed within a city/town (unified look per settlement). Biome now only SALTS
// the within-pack pick; the pack IS the settlement's identity.
export type Civilization = 'a' | 'b';

/** Deterministic per-settlement collection pick (FNV-style, no RNG). */
export function civilizationOf(siteSeed: number): Civilization {
  let h = 0x811c9dc5;
  const s = `civ|${siteSeed}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff < 0.5 ? 'a' : 'b';
}

const A = (k: string): string => `structure:civa_${k}`;
const B = (k: string): string => `structure:civb_${k}`;

const CIV_PROGRAMS: Record<Civilization, Record<Program, string[]>> = {
  a: {
    house: [A('scifi5'), A('scifi6'), A('scifi8'), A('scifi10'), A('scifi19'), A('scifi20')],
    apartment: [A('scifi2'), A('scifi4'), A('scifi7'), A('scifi9'), A('scifi11')],
    office: [A('scifi12'), A('scifi13'), A('scifi18'), A('scifi1a')],
    shop: [A('leasure_structure'), A('scifi14'), A('scifi15')],
    warehouse: [A('storage_structure')],
    factory: [A('mining_facility')],
    skyscraper: [A('scifi3'), A('scifi14'), A('scifi15'), A('scifi16'), A('scifi17')],
  },
  b: {
    house: [B('city5'), B('city8'), B('city10'), B('city13'), B('city17'), B('city18')],
    apartment: [B('city2'), B('city6'), B('city7'), B('city9')],
    office: [B('city3'), B('city11'), B('city12'), B('city1a')],
    shop: [B('leasure_structure'), B('city14')],
    warehouse: [B('output')],
    factory: [B('mining_facility')],
    skyscraper: [B('city3'), B('city4'), B('city15'), B('city16')],
  },
};

/** The pack's numbered generic towers — the only models whose proportions can
 *  carry supertall core heights (the named complexes are wide/flat and would be
 *  lattice-width-crushed to stumps downtown). Feeds preferTower. */
export const CIV_TOWERS: Record<Civilization, string[]> = {
  a: [...new Set(Object.values(CIV_PROGRAMS.a).flat())].filter((k) => /_(scifi|city)\d/.test(k)),
  b: [...new Set(Object.values(CIV_PROGRAMS.b).flat())].filter((k) => /_(scifi|city)\d/.test(k)),
};

/** Downtown casting (monumental wave 2026-07-22): a core pad (high target
 *  height) that drew a wide named complex re-casts as a seed-stable tower from
 *  the same pack — real cities put towers downtown and industry at the rim.
 *  Tower picks pass through unchanged. */
export function preferTower(civ: Civilization, key: string, h: number): string {
  if (/_(scifi|city)\d/.test(key)) return key;
  const pool = CIV_TOWERS[civ];
  return pool.length ? pool[Math.floor(h * pool.length) % pool.length] : key;
}

type PropType = 'streetlight' | 'tree' | 'bench' | 'hydrant' | 'trashcan';

// `tree` is null — foliage (EnvironmentModule) owns vegetation; we skip CityGenerator's trees.
const PROP_PALETTE: Record<PropType, string[] | null> = {
  streetlight: ['prop:solar_lamp', 'prop:street_lamp_curved', 'prop:energy_bollard'],
  bench: ['prop:antigrav_bench', 'prop:seating_pod'],
  tree: null,
  hydrant: ['prop:comms_relay_small', 'prop:info_kiosk'],
  trashcan: ['prop:trash_recycler', 'prop:supply_crate'],
};

/** The prop keys that EMIT LIGHT at night (StreetLampsModule registry). */
export const STREETLIGHT_KEYS: ReadonlySet<string> = new Set(PROP_PALETTE.streetlight!);

/** Civic landmarks placed on park parcels — per civilization (unified look). */
export const CIV_LANDMARKS: Record<Civilization, string[]> = {
  // civa_ship is NO LONGER a city landmark (sky-ship wave): as a landmark it
  // scaled to the 340 m band and lay across the whole city — the "biggest
  // structure of the city, looks like a ship" report. The ship model is the
  // SKY-SHIP now (parked 760 m up, boardable), never fabric.
  // civa_city_hall is the SKY-SHIP (the user's Ostorch giant — 72.8×21.2×90 m
  // hall, ×16 at the 340 m band = a 1.4 km slab across the city). Never fabric.
  a: [A('outpost'), A('leasure_structure'), A('survey_structure'), A('storage_structure')],
  b: [B('city4'), B('city3'), B('leasure_structure'), B('city16')],
};

/** Deterministic landmark key for a settlement's civilization. */
export function pickLandmark(civ: Civilization, h: number): string {
  const arr = CIV_LANDMARKS[civ];
  return arr[wrap(Math.floor(h * arr.length), arr.length)];
}

const BIOME_INDEX: Record<string, number> = Object.fromEntries(BIOMES.map((b, i) => [b, i]));

function wrap(i: number, n: number): number { return ((i % n) + n) % n; }

/** Deterministic `structure:*` key for a (civ, program, hash). Always returns a real key. */
export function pickStructure(civ: Civilization, biome: string, program: string, h: number): string {
  const byProgram = CIV_PROGRAMS[civ];
  const arr = byProgram[program as Program] ?? byProgram.house;
  const salt = BIOME_INDEX[biome] ?? 0;
  return arr[wrap(Math.floor(h * arr.length) + salt, arr.length)];
}

/**
 * Non-residential `BuildingFunction` → per-civilization prefab lists (the
 * archetypes emit functions like docking/reactor/barracks/shield).
 * `habitation`/`habitation_luxury` are deliberately OMITTED so they fall
 * through to the house/apartment program pools.
 */
const CIV_FUNCTIONS: Record<Civilization, Partial<Record<BuildingFunction, string[]>>> = {
  a: {
    command: [A('outpost'), A('survey_structure')], // (was civa_city_hall — the sky-ship, never a building)
    civic: [A('survey_structure'), A('outpost')],
    commerce: [A('leasure_structure'), A('scifi14')],
    market: [A('leasure_structure')],
    medical: [A('scifi12')],
    science: [A('survey_structure')],
    agriculture: [A('survey_structure'), A('scifi19')],
    industry: [A('mining_facility')],
    storage: [A('storage_structure')],
    manufacturing: [A('mining_facility'), A('storage_structure')],
    utility: [A('mining_facility')],
    defense: [A('outpost')],
    communication: [A('scifi10'), A('scifi20')],
    transit: [A('outpost'), A('storage_structure')], // (was civa_ship — the sky-ship, never a building)
    landing: [A('storage_structure')], // (was civa_ship — the sky-ship, never a building)
    recreation: [A('leasure_structure')],
    park: [A('leasure_structure')],
    docking: [A('outpost')], // (was civa_ship — the sky-ship, never a building)
    customs: [A('outpost')],
    repair: [A('mining_facility')],
    fuel: [A('mining_facility')],
    corporate: [A('scifi3'), A('scifi16')],
    datacenter: [A('scifi13')],
    entertainment: [A('leasure_structure')],
    airlock: [A('outpost')],
    shield: [A('scifi20')],
    barracks: [A('outpost'), A('scifi5')],
    armory: [A('outpost'), A('storage_structure')],
    reactor: [A('mining_facility')],
    lifesupport: [A('survey_structure')],
    concourse: [A('leasure_structure'), A('scifi14')],
  },
  b: {
    command: [B('city4')],
    civic: [B('city4'), B('city3')],
    commerce: [B('leasure_structure'), B('city14')],
    market: [B('leasure_structure')],
    medical: [B('city11')],
    science: [B('survey_structure')],
    agriculture: [B('survey_structure'), B('city17')],
    industry: [B('mining_facility')],
    storage: [B('output')],
    manufacturing: [B('mining_facility'), B('output')],
    utility: [B('mining_facility')],
    defense: [B('city16')],
    communication: [B('city10'), B('city18')],
    transit: [B('city15')],
    landing: [B('city15')],
    recreation: [B('leasure_structure')],
    park: [B('leasure_structure')],
    docking: [B('city15')],
    customs: [B('city16')],
    repair: [B('mining_facility')],
    fuel: [B('mining_facility')],
    corporate: [B('city3'), B('city16')],
    datacenter: [B('city12')],
    entertainment: [B('leasure_structure')],
    airlock: [B('city16')],
    shield: [B('city18')],
    barracks: [B('city16'), B('city5')],
    armory: [B('city16'), B('output')],
    reactor: [B('mining_facility')],
    lifesupport: [B('survey_structure')],
    concourse: [B('leasure_structure'), B('city14')],
  },
};

/** Building-interiors wave: the pack keys are opaque (`civa_scifi5`,
 *  `civb_city13`) so name regexes can't classify them — but the PROGRAM tables
 *  above are the semantic truth. Enterable = living quarters + base city
 *  structures (house/apartment/office/skyscraper programs, command/civic
 *  functions); industry (factory/warehouse/storage/utility) stays sealed. */
export const ENTERABLE_CIV_KEYS: ReadonlySet<string> = new Set(
  (['a', 'b'] as const).flatMap((c) => [
    ...CIV_PROGRAMS[c].house, ...CIV_PROGRAMS[c].apartment,
    ...CIV_PROGRAMS[c].office, ...CIV_PROGRAMS[c].skyscraper,
    ...(CIV_FUNCTIONS[c].command ?? []), ...(CIV_FUNCTIONS[c].civic ?? []),
  ]),
);

/** Human display name for an opaque civ-pack key (interiors door prompt) —
 *  derived from the program tables, most-specific program first. Null for
 *  non-civ keys (their names read fine after prefix cleanup). */
export function civDisplayName(key: string): string | null {
  for (const c of ['a', 'b'] as const) {
    const P = CIV_PROGRAMS[c];
    if ((CIV_FUNCTIONS[c].command ?? []).includes(key) || (CIV_FUNCTIONS[c].civic ?? []).includes(key)) return 'civic hall';
    if (P.skyscraper.includes(key)) return 'highrise tower';
    if (P.apartment.includes(key)) return 'apartment tower';
    if (P.office.includes(key)) return 'office block';
    if (P.house.includes(key)) return 'residence';
    if (P.shop.includes(key)) return 'commerce hall';
  }
  return null;
}

/** Prefer the building's FUNCTION for a civ-specific prefab; fall back to the program pools. */
export function pickStructureForFunction(civ: Civilization, biome: string, fn: string | undefined, type: string, h: number): string {
  const list = fn ? CIV_FUNCTIONS[civ][fn as BuildingFunction] : undefined;
  if (list && list.length > 0) {
    const salt = BIOME_INDEX[biome] ?? 0;
    return list[wrap(Math.floor(h * list.length) + salt, list.length)];
  }
  return pickStructure(civ, biome, type, h);
}

/** Deterministic `prop:*` key for a (biome, propType, hash), or null to skip (e.g. `tree`). */
export function pickProp(biome: string, propType: string, h: number): string | null {
  const arr = PROP_PALETTE[propType as PropType];
  if (!arr || arr.length === 0) return null;
  // biome nudges the choice so prop styling varies subtly by region, still deterministic.
  const salt = BIOME_INDEX[biome] ?? 0;
  return arr[wrap(Math.floor(h * arr.length) + salt, arr.length)];
}
